/**
 * TemplatesPage - Browse and use workflow templates
 * ZAPHUB 2.0 - Template library, starter packs, categories
 */

import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  FileText,
  Search,
  Star,
  Download,
  Clock,
  TrendingUp,
  Sparkles,
  Rocket,
  Users,
  Code,
  DollarSign,
  Megaphone,
  Headphones,
  Share2,
  Settings,
  Package,
  CheckCircle,
  Play,
  Eye,
} from 'lucide-react';
import type { WorkflowTemplate, WorkflowTemplateCategory, StarterPack } from '@shared/types';

export const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [categories, setCategories] = useState<WorkflowTemplateCategory[]>([]);
  const [starterPacks, setStarterPacks] = useState<StarterPack[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
    fetchStarterPacks();
  }, []);

  const fetchTemplates = async () => {
    // Mock - in production, call API
    const mockTemplates: WorkflowTemplate[] = [
      {
        id: 'email-welcome-series',
        name: 'Welcome Email Series',
        description: 'Automated 5-email welcome sequence for new customers',
        category: 'marketing',
        tags: ['email', 'onboarding', 'engagement'],
        nodes: [],
        difficulty: 'beginner',
        estimatedSetupTime: 15,
        usageCount: 1250,
        rating: 4.8,
        reviewCount: 234,
        author: 'ZAPHUB Team',
        authorVerified: true,
        createdAt: new Date('2024-01-15'),
        updatedAt: new Date('2024-11-20'),
        features: [
          'Personalized emails',
          'Smart timing (24h, 48h, 7d, 14d, 30d)',
          'A/B testing ready',
          'Conversion tracking',
        ],
        requirements: ['Gmail or SendGrid integration', 'Contact list'],
        compatibility: ['Gmail', 'SendGrid', 'Mailchimp'],
      },
      {
        id: 'lead-scoring',
        name: 'AI Lead Scoring & Qualification',
        description: 'Automatically score and qualify leads using AI analysis',
        category: 'marketing',
        tags: ['ai', 'sales', 'lead-generation'],
        nodes: [],
        difficulty: 'intermediate',
        estimatedSetupTime: 30,
        usageCount: 890,
        rating: 4.9,
        reviewCount: 178,
        author: 'ZAPHUB Team',
        authorVerified: true,
        createdAt: new Date('2024-02-10'),
        updatedAt: new Date('2024-12-01'),
        features: [
          'AI-powered scoring (GPT-4)',
          'CRM integration',
          'Auto-assignment to sales reps',
          'Follow-up reminders',
        ],
        requirements: ['HubSpot or Salesforce', 'AI Agent configured'],
        compatibility: ['HubSpot', 'Salesforce', 'Notion'],
      },
      {
        id: 'cicd-pipeline',
        name: 'CI/CD Pipeline Automation',
        description: 'Automated testing, building, and deployment pipeline',
        category: 'development',
        tags: ['devops', 'ci-cd', 'deployment'],
        nodes: [],
        difficulty: 'advanced',
        estimatedSetupTime: 90,
        usageCount: 1800,
        rating: 4.9,
        reviewCount: 312,
        author: 'ZAPHUB Team',
        authorVerified: true,
        createdAt: new Date('2024-01-10'),
        updatedAt: new Date('2024-12-10'),
        features: [
          'GitHub integration',
          'Automated testing',
          'Build optimization',
          'Deployment to staging/prod',
          'Rollback capability',
          'Slack notifications',
        ],
        requirements: ['GitHub account', 'Deployment environment'],
        compatibility: ['GitHub', 'GitLab', 'AWS', 'Slack'],
      },
    ];

    setTemplates(mockTemplates);
  };

  const fetchCategories = async () => {
    const mockCategories: WorkflowTemplateCategory[] = [
      {
        id: 'marketing',
        name: 'Marketing & Sales',
        description: 'Email campaigns, lead generation, customer engagement',
        icon: 'megaphone',
        color: '#FF6B6B',
        templateCount: 25,
      },
      {
        id: 'hr',
        name: 'HR & Recruitment',
        description: 'Employee onboarding, payroll, benefits management',
        icon: 'users',
        color: '#4ECDC4',
        templateCount: 18,
      },
      {
        id: 'finance',
        name: 'Finance & Accounting',
        description: 'Invoicing, expense tracking, financial reports',
        icon: 'dollar-sign',
        color: '#95E1D3',
        templateCount: 22,
      },
      {
        id: 'development',
        name: 'Development & IT',
        description: 'CI/CD, deployments, monitoring, incidents',
        icon: 'code',
        color: '#AA96DA',
        templateCount: 35,
      },
      {
        id: 'operations',
        name: 'Operations',
        description: 'Asset management, inventory, logistics',
        icon: 'settings',
        color: '#F38181',
        templateCount: 30,
      },
    ];

    setCategories(mockCategories);
  };

  const fetchStarterPacks = async () => {
    const mockPacks: StarterPack[] = [
      {
        id: 'startup-essentials',
        name: 'Startup Essentials',
        description: 'Everything a startup needs to automate operations',
        icon: 'rocket',
        color: '#FF6B6B',
        templateIds: ['email-welcome-series', 'lead-scoring', 'cicd-pipeline'],
        price: 0,
        isPremium: false,
        usageCount: 3500,
        rating: 4.9,
        reviewCount: 678,
      },
      {
        id: 'devops-master',
        name: 'DevOps Master Pack',
        description: 'Professional DevOps workflows for development teams',
        icon: 'code',
        color: '#AA96DA',
        templateIds: ['cicd-pipeline'],
        price: 149,
        isPremium: true,
        usageCount: 1200,
        rating: 4.9,
        reviewCount: 267,
      },
    ];

    setStarterPacks(mockPacks);
  };

  const handleUseTemplate = (template: WorkflowTemplate) => {
    // In production, clone template to workspace
    console.log('Using template:', template.id);
    alert(`Template "${template.name}" added to your workflows!`);
  };

  const handlePreview = (template: WorkflowTemplate) => {
    setSelectedTemplate(template);
    setShowPreview(true);
  };

  const filteredTemplates = templates.filter((template) => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const getCategoryIcon = (iconName: string) => {
    const icons: { [key: string]: React.FC<any> } = {
      megaphone: Megaphone,
      users: Users,
      'dollar-sign': DollarSign,
      code: Code,
      settings: Settings,
      headphones: Headphones,
      'share-2': Share2,
    };
    return icons[iconName] || FileText;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-700';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-700';
      case 'advanced':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl mb-2">Workflow Templates</h1>
        <p className="text-gray-600">
          Start with proven templates or browse starter packs for your industry
        </p>
      </div>

      {/* Search & Filters */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="templates" className="w-full">
        <TabsList>
          <TabsTrigger value="templates">
            <FileText className="h-4 w-4 mr-2" />
            Templates ({templates.length})
          </TabsTrigger>
          <TabsTrigger value="starter-packs">
            <Package className="h-4 w-4 mr-2" />
            Starter Packs ({starterPacks.length})
          </TabsTrigger>
          <TabsTrigger value="categories">
            <Settings className="h-4 w-4 mr-2" />
            Categories ({categories.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-6">
          {/* Category Filter */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
            >
              All Templates
            </Button>
            {categories.map((category) => {
              const Icon = getCategoryIcon(category.icon);
              return (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {category.name} ({category.templateCount})
                </Button>
              );
            })}
          </div>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <Card key={template.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg mb-2">{template.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  </div>
                  {template.authorVerified && (
                    <Badge variant="secondary" className="ml-2">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    <span>{template.rating.toFixed(1)}</span>
                    <span className="text-gray-400">({template.reviewCount})</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Download className="h-4 w-4" />
                    <span>{template.usageCount.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{template.estimatedSetupTime}m</span>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge className={getDifficultyColor(template.difficulty)}>
                    {template.difficulty}
                  </Badge>
                  {template.tags.slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>

                {/* Features */}
                <div className="mb-4">
                  <div className="text-sm text-gray-600 mb-2">Features:</div>
                  <ul className="space-y-1">
                    {template.features?.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handlePreview(template)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                  <Button size="sm" className="flex-1" onClick={() => handleUseTemplate(template)}>
                    <Play className="h-4 w-4 mr-2" />
                    Use Template
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <Card className="p-12 text-center">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl mb-2">No templates found</h3>
              <p className="text-gray-600">
                Try adjusting your search or category filter
              </p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="starter-packs" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {starterPacks.map((pack) => (
              <Card key={pack.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-12 w-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: pack.color + '20' }}
                    >
                      <Rocket className="h-6 w-6" style={{ color: pack.color }} />
                    </div>
                    <div>
                      <h3 className="text-lg mb-1">{pack.name}</h3>
                      <p className="text-sm text-gray-600">{pack.description}</p>
                    </div>
                  </div>
                  {pack.isPremium && (
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Premium
                    </Badge>
                  )}
                </div>

                <div className="mb-4">
                  <div className="text-sm text-gray-600 mb-2">
                    Includes {pack.templateIds.length} templates
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      <span>{pack.rating.toFixed(1)}</span>
                      <span className="text-gray-400">({pack.reviewCount})</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Download className="h-4 w-4" />
                      <span>{pack.usageCount.toLocaleString()} uses</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    {pack.price === 0 ? (
                      <span className="text-2xl">Free</span>
                    ) : (
                      <div>
                        <span className="text-2xl">${pack.price}</span>
                        <span className="text-sm text-gray-600"> one-time</span>
                      </div>
                    )}
                  </div>
                  <Button>
                    <Download className="h-4 w-4 mr-2" />
                    Get Pack
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => {
              const Icon = getCategoryIcon(category.icon);
              return (
                <Card
                  key={category.id}
                  className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => {
                    setSelectedCategory(category.id);
                    // Switch to templates tab
                    document.querySelector('[value="templates"]')?.dispatchEvent(new MouseEvent('click'));
                  }}
                >
                  <div
                    className="h-12 w-12 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: category.color + '20' }}
                  >
                    <Icon className="h-6 w-6" style={{ color: category.color }} />
                  </div>
                  <h3 className="text-lg mb-2">{category.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{category.description}</p>
                  <Badge variant="secondary">{category.templateCount} templates</Badge>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name}</DialogTitle>
            <DialogDescription>{selectedTemplate?.description}</DialogDescription>
          </DialogHeader>

          {selectedTemplate && (
            <div className="space-y-6 py-4 max-h-[600px] overflow-y-auto">
              {/* Stats */}
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                  <span className="text-lg">{selectedTemplate.rating.toFixed(1)}</span>
                  <span className="text-gray-500">({selectedTemplate.reviewCount} reviews)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  <span>{selectedTemplate.usageCount.toLocaleString()} uses</span>
                </div>
                <Badge className={getDifficultyColor(selectedTemplate.difficulty)}>
                  {selectedTemplate.difficulty}
                </Badge>
              </div>

              {/* Features */}
              <div>
                <h4 className="text-lg mb-3">Features</h4>
                <ul className="space-y-2">
                  {selectedTemplate.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Requirements */}
              {selectedTemplate.requirements && selectedTemplate.requirements.length > 0 && (
                <div>
                  <h4 className="text-lg mb-3">Requirements</h4>
                  <ul className="space-y-2">
                    {selectedTemplate.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-600">
                        <span className="text-purple-500">•</span>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Compatibility */}
              {selectedTemplate.compatibility && selectedTemplate.compatibility.length > 0 && (
                <div>
                  <h4 className="text-lg mb-3">Compatible With</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.compatibility.map((comp) => (
                      <Badge key={comp} variant="secondary">
                        {comp}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Author */}
              <div className="flex items-center gap-2 pt-4 border-t">
                <span className="text-gray-600">Created by:</span>
                <span>{selectedTemplate.author}</span>
                {selectedTemplate.authorVerified && (
                  <Badge variant="secondary">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
            <Button onClick={() => selectedTemplate && handleUseTemplate(selectedTemplate)}>
              <Play className="h-4 w-4 mr-2" />
              Use This Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
